<script setup lang="ts">
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useTermsDocumentVersions } from "@/composables/models/terms/useTerms";
import type { TPendingTerm } from "@/stores/pendingTermsStore";

const props = defineProps<{ term: TPendingTerm }>();

/**
 * Reported upward because the dialog title is an aggregate ("Updated Policy Documents" only if
 * *something* here is a revision), which no single row can decide on its own.
 */
const emit = defineEmits<{ "update:revision": [docId: string, isRevision: boolean] }>();

const { t } = useI18n();

/**
 * The API exposes no "which version did this user accept" field, so a revision is derived from the
 * document's version list: a per-version `is_accepted` that is true anywhere means they signed an
 * earlier version.
 *
 * Deliberately NOT "did they accept the newest version" — a document only reaches this dialog
 * because its current version is unaccepted, so that test is false for every row by construction
 * and the badge would never show. What distinguishes a revision from a first-time agreement is
 * whether any *prior* signature exists.
 *
 * Each row owns this query so the list stays a plain `v-for` over the store: one composable per
 * document, keyed and torn down by Vue, instead of the parent hand-rolling a fan-out.
 */
const { data: versions, isLoading } = useTermsDocumentVersions(
  computed(() => ({ doc_pk: props.term.id }))
);

/**
 * Unresolved means "no badge", not "no revision" — labelling a brand-new user's first agreement as
 * an update is the exact thing this lookup exists to prevent, so it stays silent until it knows.
 */
const isRevision = computed(
  () =>
    !isLoading.value &&
    (versions.value?.length ?? 0) > 1 &&
    !!versions.value?.some((version) => version.is_accepted)
);

watch(isRevision, (revision) => emit("update:revision", props.term.id, revision), {
  immediate: true,
});
</script>

<template>
  <div
    class="tw:flex tw:items-center tw:gap-3 tw:rounded-lg tw:border tw:border-on-surface/10 tw:px-3.5 tw:py-3"
    :data-testid="`pending-term-${term.id}`"
  >
    <span
      class="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:bg-secondary/10"
    >
      <v-icon
        icon="mdi-file-document-outline"
        size="17"
        color="secondary"
      />
    </span>

    <span class="tw:flex tw:min-w-0 tw:flex-1 tw:flex-wrap tw:items-center tw:gap-x-2 tw:gap-y-1">
      <span class="tw:text-sm tw:leading-tight tw:font-semibold">{{ term.title }}</span>
      <v-chip
        v-if="isRevision"
        size="x-small"
        variant="tonal"
        color="warning"
        label
        data-testid="pending-term-updated-badge"
        rounded="pill"
      >
        {{ t("generic.dialog.pendingTerms.updatedBadge") }}
      </v-chip>
    </span>

    <a
      :href="`/documents/${term.id}`"
      target="_blank"
      rel="noopener noreferrer"
      class="tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:text-sm tw:font-semibold tw:text-secondary tw:hover:underline"
      :data-testid="`pending-term-read-${term.id}`"
    >
      {{ t("generic.dialog.pendingTerms.readAction") }}
      <v-icon
        icon="mdi-open-in-new"
        size="13"
      />
    </a>
  </div>
</template>
